import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, GraduationCap, ArrowLeft, ShieldCheck, Calendar } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import { trackStudentEnquiry } from '@/lib/analytics';
import Seo from '@/components/shared/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * V4 PLATFORM - STUDENT ENQUIRY / BOOKING REQUEST
 * Complete clean rebuild with premium Winhouse brand styling
 * Supports both general enquiries and tutor-specific booking requests
 */
const StudentEnquiryV4 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    email: '',
    phone: '',
    programType: '',
    subject: '',
    yearLevel: '',
    preferredFormat: '',
    preferredTimes: '',
    tutorPreference: 'match',
    specificTutorId: '',
    notes: ''
  });
  
  const [tutors, setTutors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBookingMode, setIsBookingMode] = useState(false);
  const [prefilledTutor, setPrefilledTutor] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    fetchPublicTutors();
    
    // Phase 3A: Check if tutor was pre-filled from profile/card
    if (location.state?.tutorId && location.state?.tutorName) {
      setIsBookingMode(true);
      setPrefilledTutor({
        id: location.state.tutorId,
        name: location.state.tutorName
      });
      // Auto-select specific tutor preference
      setFormData(prev => ({
        ...prev,
        tutorPreference: 'specific',
        specificTutorId: location.state.tutorId
      }));
    }
  }, [location.state]);
  
  const fetchPublicTutors = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v4/public/tutors`);
      const data = await response.json();
      if (data.success) {
        setTutors(data.tutors || []);
      }
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = () => {
    if (!formData.studentName.trim()) {
      toast.error('Please enter student name');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter phone number');
      return false;
    }
    if (!formData.programType) {
      toast.error('Please select program type');
      return false;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter subject');
      return false;
    }
    if (!formData.yearLevel.trim()) {
      toast.error('Please enter year level');
      return false;
    }
    if (!formData.preferredFormat) {
      toast.error('Please select preferred format');
      return false;
    }
    if (!formData.preferredTimes.trim()) {
      toast.error('Please enter preferred times');
      return false;
    }
    if (formData.tutorPreference === 'specific' && !formData.specificTutorId) {
      toast.error('Please select a tutor');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('student_name', formData.studentName.trim());
      formDataToSend.append('parent_name', formData.parentName.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('program_type', formData.programType);
      formDataToSend.append('subject', formData.subject.trim());
      formDataToSend.append('year_level', formData.yearLevel.trim());
      formDataToSend.append('preferred_format', formData.preferredFormat);
      formDataToSend.append('preferred_times', formData.preferredTimes.trim());
      formDataToSend.append('tutor_preference', formData.tutorPreference);
      if (formData.tutorPreference === 'specific') {
        formDataToSend.append('specific_tutor_id', formData.specificTutorId);
      }
      if (formData.notes.trim()) {
        formDataToSend.append('notes', formData.notes.trim());
      }
      
      const response = await fetch(`${BACKEND_URL}/api/v4/student/enquire`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Submission failed');
      }
      
      toast.success(data.message || 'Enquiry submitted successfully!');
      try {
        trackStudentEnquiry({
          program_type: formData.programType,
          subject: formData.subject,
          year_level: formData.yearLevel,
          preferred_format: formData.preferredFormat,
        });
      } catch { /* noop */ }
      
      setTimeout(() => {
        navigate('/enrollment-success', {
          state: {
            studentName: formData.studentName,
            enquiryId: data.enquiry_id
          }
        });
      }, 1000);
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Failed to submit enquiry');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Book a Free Trial | Winhouse Tutors"
        description="Tell us your goals and subject. We'll match you with the right tutor for VCE, IELTS, Selective Entry and more."
        path="/get-started"
      />
      {/* Global Navigation */}
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      {/* Page Hero Section */}
      <div className="border-b shadow-sm" style={{background: '#FFFFFF', borderBottomColor: 'rgba(183, 191, 114, 0.20)', paddingTop: '5rem'}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-1" style={{color: '#000000'}}>
                {isBookingMode ? 'Book Your Free Consultation' : 'Find Your Perfect Tutor'}
              </h1>
              <p className="text-base md:text-lg text-gray-600">
                {isBookingMode 
                  ? `Request a session with ${prefilledTutor?.name || 'our expert tutor'}`
                  : 'Tell us your needs and we\'ll match you with the best tutor'
                }
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate(isBookingMode ? '/tutors' : '/')}
              className="hover:bg-gray-100 hidden sm:flex"
              style={{borderColor: 'rgba(183, 191, 114, 0.40)', color: '#333333'}}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> {isBookingMode ? 'Back to Tutors' : 'Home'}
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Phase 3A: Show selected tutor badge in booking mode */}
        {isBookingMode && prefilledTutor && (
          <div className="mb-6">
            <Card className="border-2" style={{borderColor: 'rgba(183, 191, 114, 0.30)', background: 'rgba(183, 191, 114, 0.08)'}}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6" style={{color: '#006C69'}} />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Booking with:</p>
                      <p className="text-lg font-bold text-gray-900">{prefilledTutor.name}</p>
                    </div>
                  </div>
                  <Badge className="px-3 py-1 text-white border-0" style={{background: '#006C69'}}>
                    ✓ Verified Tutor
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        <Card className="shadow-2xl border-2" style={{borderColor: 'rgba(183, 191, 114, 0.20)'}}>
          <CardHeader className="space-y-2 rounded-t-lg" style={{background: '#B7BF72'}}>
            <CardTitle className="text-3xl font-bold" style={{color: '#000000'}}>
              {isBookingMode ? 'Consultation Request' : 'Find a Tutor'}
            </CardTitle>
            <CardDescription className="text-base" style={{color: '#333333'}}>
              {isBookingMode 
                ? 'Fill in your details below and we\'ll arrange your free consultation'
                : 'Tell us about your learning needs and we\'ll match you with the perfect tutor'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="studentName" className="text-base font-semibold">
                    Student Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="studentName"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleInputChange}
                    placeholder="Student's full name"
                    className="h-12"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="parentName" className="text-base font-semibold">
                    Parent/Guardian Name
                  </Label>
                  <Input
                    id="parentName"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    placeholder="Parent's name (optional)"
                    className="h-12"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base font-semibold">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="h-12"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-base font-semibold">
                    Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+61 400 000 000"
                    className="h-12"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Program Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.programType}
                    onValueChange={(value) => handleSelectChange('programType', value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select program" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VCE">VCE</SelectItem>
                      <SelectItem value="IELTS">IELTS</SelectItem>
                      <SelectItem value="Primary">Primary School</SelectItem>
                      <SelectItem value="HighSchool">High School</SelectItem>
                      <SelectItem value="University">University</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-base font-semibold">
                    Subject <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="e.g., Mathematics"
                    className="h-12"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="yearLevel" className="text-base font-semibold">
                    Year Level <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="yearLevel"
                    name="yearLevel"
                    value={formData.yearLevel}
                    onChange={handleInputChange}
                    placeholder="e.g., Year 12"
                    className="h-12"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-base font-semibold">
                    Preferred Format <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.preferredFormat}
                    onValueChange={(value) => handleSelectChange('preferredFormat', value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Online">Online</SelectItem>
                      <SelectItem value="In-Person">In-Person</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="preferredTimes" className="text-base font-semibold">
                  Preferred Times <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="preferredTimes"
                  name="preferredTimes"
                  value={formData.preferredTimes}
                  onChange={handleInputChange}
                  placeholder="e.g., Weekday evenings, Saturday mornings"
                  className="h-12"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="space-y-4">
                <Label className="text-base font-semibold">Tutor Preference</Label>
                <RadioGroup
                  value={formData.tutorPreference}
                  onValueChange={(value) => handleSelectChange('tutorPreference', value)}
                  disabled={isSubmitting}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="match" id="match" />
                    <Label htmlFor="match" className="cursor-pointer font-normal">
                      Match me with the best tutor
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="specific" id="specific" />
                    <Label htmlFor="specific" className="cursor-pointer font-normal">
                      I want a specific tutor
                    </Label>
                  </div>
                </RadioGroup>
                
                {formData.tutorPreference === 'specific' && (
                  <Select
                    value={formData.specificTutorId}
                    onValueChange={(value) => handleSelectChange('specificTutorId', value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select a tutor" />
                    </SelectTrigger>
                    <SelectContent>
                      {tutors.map(tutor => (
                        <SelectItem key={tutor.id} value={tutor.id}>
                          {tutor.full_name} - {tutor.subjects.join(', ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-base font-semibold">
                  {isBookingMode ? 'Learning Goals / Message' : 'Additional Notes'} (Optional)
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder={isBookingMode 
                    ? "What are your learning goals? Any specific areas you'd like to focus on?"
                    : "Any specific requirements or goals..."
                  }
                  rows={4}
                  className="resize-none"
                  disabled={isSubmitting}
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                style={{background: '#B7BF72', color: '#000000'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#000000';
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#B7BF72';
                  e.currentTarget.style.color = '#000000';
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {isBookingMode ? 'Submitting Request...' : 'Submitting Enquiry...'}
                  </>
                ) : (
                  isBookingMode ? 'Confirm Booking Request' : 'Submit Enquiry'
                )}
              </Button>
              
              {/* Phase 3A: Platform trust indicator */}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 bg-gray-50 py-3 px-4 rounded-lg border border-gray-200 mt-4">
                <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="font-medium text-center">All sessions managed securely through Winhouse Tutors</span>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* Global Footer */}
      <Footer />
      
      {/* Shared Floating Action Buttons */}
      <FloatingActionButtons />
    </div>
  );
};

export default StudentEnquiryV4;